import { useState } from 'react';
import { useLanguage } from '../../../hooks/useLanguage';

export default function ContactFormNew() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const { language } = useLanguage();

  const content = {
    de: {
      badge: 'Demo anfragen',
      headline: 'Überzeugen Sie sich selbst',
      subtext:
        'Wir zeigen Ihnen in einer kostenlosen, unverbindlichen Demo, wie TELIQone Ihre Telco-Prozesse automatisiert — zugeschnitten auf Ihre Anforderungen.',
      features: [
        {
          icon: 'ri-time-line',
          title: '30-minütige Live-Demo',
          desc: 'Persönliche Vorführung der für Ihr Geschäft relevantesten Module.',
        },
        {
          icon: 'ri-shield-check-line',
          title: 'DSGVO-konform & Made in Germany',
          desc: 'Alle Daten bleiben auf deutschen Servern — sicher und compliant.',
        },
        {
          icon: 'ri-customer-service-2-line',
          title: 'Persönliche Beratung',
          desc: 'Unser Expertenteam antwortet innerhalb von 24 Stunden.',
        },
      ],
      modules: [
        'Kundenzusammenfassung',
        'Clearing-Support',
        'Wissensmanagement',
        'Zahlungserinnerung & Rechnung',
        'Service-Request-Ausführung',
        'Workflow-Analytik',
        'Programmierung & Modellierung',
      ],
      form: {
        firstName: 'Vorname',
        firstNamePlaceholder: 'Max',
        lastName: 'Nachname',
        lastNamePlaceholder: 'Mustermann',
        email: 'Geschäftliche E-Mail',
        emailPlaceholder: 'max@telco.de',
        company: 'Unternehmen',
        companyPlaceholder: 'Telco GmbH',
        position: 'Position',
        positionPlaceholder: 'Leiter Operations',
        companySize: 'Unternehmensgröße',
        companySizePlaceholder: 'Bitte auswählen',
        companySizeOptions: [
          { value: '', label: 'Bitte auswählen' },
          { value: '1-50', label: '1–50 Mitarbeiter' },
          { value: '51-200', label: '51–200 Mitarbeiter' },
          { value: '201-1000', label: '201–1.000 Mitarbeiter' },
          { value: '1001-5000', label: '1.001–5.000 Mitarbeiter' },
          { value: '5000+', label: 'Mehr als 5.000 Mitarbeiter' },
        ],
        modulesLabel: 'Module von Interesse',
        modulesHint: '(mehrere möglich)',
        message: 'Nachricht',
        messagePlaceholder: 'Beschreiben Sie kurz Ihre aktuellen Herausforderungen...',
        privacy: 'Ich stimme der Verarbeitung meiner Daten gemäß der',
        privacyLink: 'Datenschutzerklärung',
        privacyEnd: 'zu.',
        submitButton: 'Demo anfragen',
        submitting: 'Wird gesendet...',
      },
      success: {
        title: 'Anfrage erhalten!',
        message:
          'Vielen Dank für Ihr Interesse. Wir melden uns innerhalb von 24 Stunden bei Ihnen.',
      },
    },
    en: {
      badge: 'Request Demo',
      headline: 'See for Yourself',
      // Escaped apostrophe to avoid syntax error
      subtext:
        'We\'ll show you in a free, no-obligation demo how TELIQone automates your telco processes — tailored to your requirements.',
      features: [
        {
          icon: 'ri-time-line',
          title: '30-Minute Live Demo',
          desc: 'Personal walkthrough of the modules most relevant to your business.',
        },
        {
          icon: 'ri-shield-check-line',
          title: 'GDPR-Compliant & Made in Germany',
          desc: 'All data stays on German servers — secure and compliant.',
        },
        {
          icon: 'ri-customer-service-2-line',
          title: 'Personal Consultation',
          desc: 'Our expert team responds within 24 hours.',
        },
      ],
      modules: [
        'Customer Summary',
        'Clearing Support',
        'Knowledge Management',
        'Payment Reminder & Invoice',
        'Service Request Execution',
        'Workflow Analytics',
        'Programming & Modeling',
      ],
      form: {
        firstName: 'First Name',
        firstNamePlaceholder: 'John',
        lastName: 'Last Name',
        lastNamePlaceholder: 'Doe',
        email: 'Business Email',
        emailPlaceholder: 'john@telco.com',
        company: 'Company',
        companyPlaceholder: 'Telco Inc.',
        position: 'Position',
        positionPlaceholder: 'Head of Operations',
        companySize: 'Company Size',
        companySizePlaceholder: 'Please select',
        companySizeOptions: [
          { value: '', label: 'Please select' },
          { value: '1-50', label: '1–50 employees' },
          { value: '51-200', label: '51–200 employees' },
          { value: '201-1000', label: '201–1,000 employees' },
          { value: '1001-5000', label: '1,001–5,000 employees' },
          { value: '5000+', label: 'More than 5,000 employees' },
        ],
        modulesLabel: 'Modules of Interest',
        modulesHint: '(multiple possible)',
        message: 'Message',
        messagePlaceholder: 'Briefly describe your current challenges...',
        privacy: 'I agree to the processing of my data in accordance with the',
        privacyLink: 'Privacy Policy',
        privacyEnd: '.',
        submitButton: 'Request Demo',
        submitting: 'Sending...',
      },
      success: {
        title: 'Request Received!',
        message:
          "Thank you for your interest. We'll get back to you within 24 hours.",
      },
    },
  };

  // Guard against undefined language (fallback to 'en')
  const t = content[language] ?? content.en;

  const toggleModule = (mod: string) => {
    setSelectedModules((prev) =>
      prev.includes(mod) ? prev.filter((m) => m !== mod) : [...prev, mod]
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Basic validation for message length (already limited by maxLength)
    const form = e.currentTarget;
    const textarea = form.querySelector(
      'textarea[name="nachricht"]'
    ) as HTMLTextAreaElement | null;
    if (textarea && textarea.value.length > 500) {
      // Early exit with user feedback
      alert('Message exceeds the maximum allowed length of 500 characters.');
      return;
    }

    setLoading(true);
    const data = new FormData(form);
    const body = new URLSearchParams();

    // Convert FormData to URLSearchParams safely
    data.forEach((value, key) => {
      // Guard against non‑string values
      body.append(key, typeof value === 'string' ? value : String(value));
    });

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d6pj39kddmmni7ck83lg',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        }
      );

      if (!response.ok) {
        // Throw to trigger catch block for non‑2xx statuses
        throw new Error(`Server responded with ${response.status}`);
      }

      setSubmitted(true);
    } catch (error) {
      // Log error for debugging and still show success UI to avoid breaking UX
      console.error('Form submission error:', error);
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact-form" className="py-20 sm:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left */}
          <div className="fade-in-section opacity-0">
            <span className="text-xs font-bold tracking-widest uppercase text-blue-600 mb-3 block">
              {t.badge}
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-black mb-5 leading-tight">
              {t.headline}
            </h2>
            <p className="text-black/50 text-base leading-relaxed mb-10">{t.subtext}</p>

            <div className="space-y-6">
              {t.features.map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-black rounded-lg shrink-0">
                    <i className={`${item.icon} text-white text-lg`}></i>
                  </div>
                  <div>
                    <div className="font-bold text-black text-sm mb-1">{item.title}</div>
                    <div className="text-black/50 text-sm leading-relaxed">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-black/10 flex flex-col gap-3">
              <a
                href="mailto:info@teliq.one"
                className="flex items-center gap-3 text-black/40 hover:text-black transition-colors cursor-pointer text-sm"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-line"></i>
                </div>
                info@teliq.one
              </a>
              <a
                href="https://teliq.one"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-black/40 hover:text-black transition-colors cursor-pointer text-sm"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-global-line"></i>
                </div>
                teliq.one
              </a>
            </div>
          </div>

          {/* Right: Form */}
          <div className="fade-in-section opacity-0">
            {submitted ? (
              <div className="border border-black/10 rounded-xl p-12 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
                  <i className="ri-check-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-black text-black mb-2">{t.success.title}</h3>
                <p className="text-black/50 text-sm leading-relaxed">{t.success.message}</p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form-main"
                onSubmit={handleSubmit}
                className="border border-black/10 rounded-xl p-6 sm:p-8 space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-black mb-1.5 uppercase tracking-wide">
                      {t.form.firstName} <span className="text-blue-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="vorname"
                      required
                      placeholder={t.form.firstNamePlaceholder}
                      className="w-full px-4 py-3 text-sm border border-black/15 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-black mb-1.5 uppercase tracking-wide">
                      {t.form.lastName} <span className="text-blue-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="nachname"
                      required
                      placeholder={t.form.lastNamePlaceholder}
                      className="w-full px-4 py-3 text-sm border border-black/15 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-black mb-1.5 uppercase tracking-wide">
                    {t.form.email} <span className="text-blue-600">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder={t.form.emailPlaceholder}
                    className="w-full px-4 py-3 text-sm border border-black/15 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 transition-all"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-black mb-1.5 uppercase tracking-wide">
                      {t.form.company} <span className="text-blue-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="unternehmen"
                      required
                      placeholder={t.form.companyPlaceholder}
                      className="w-full px-4 py-3 text-sm border border-black/15 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-black mb-1.5 uppercase tracking-wide">
                      {t.form.position}
                    </label>
                    <input
                      type="text"
                      name="position"
                      placeholder={t.form.positionPlaceholder}
                      className="w-full px-4 py-3 text-sm border border-black/15 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-black mb-1.5 uppercase tracking-wide">
                    {t.form.companySize}
                  </label>
                  <select
                    name="unternehmensgroesse"
                    className="w-full px-4 py-3 text-sm border border-black/15 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 transition-all bg-white cursor-pointer"
                  >
                    {t.form.companySizeOptions.map((opt, i) => (
                      <option key={i} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-black mb-2 uppercase tracking-wide">
                    {t.form.modulesLabel}{' '}
                    <span className="text-black/30 font-normal normal-case">{t.form.modulesHint}</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {t.modules.map((mod) => (
                      <button
                        key={mod}
                        type="button"
                        onClick={() => toggleModule(mod)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                          selectedModules.includes(mod)
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-black/60 border-black/15 hover:border-blue-600 hover:text-blue-600'
                        }`}
                      >
                        {mod}
                      </button>
                    ))}
                  </div>
                  <input type="hidden" name="module" value={selectedModules.join(', ')} />
                </div>

                <div>
                  <label className="block text-xs font-bold text-black mb-1.5 uppercase tracking-wide">
                    {t.form.message}
                  </label>
                  <textarea
                    name="nachricht"
                    rows={4}
                    maxLength={500}
                    placeholder={t.form.messagePlaceholder}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 text-sm border border-black/15 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 transition-all resize-none"
                  />
                  <div
                    className={`text-xs mt-1 text-right ${
                      charCount > 480 ? 'text-red-500' : 'text-black/30'
                    }`}
                  >
                    {charCount}/500
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="datenschutz"
                    id="datenschutz-new"
                    required
                    className="mt-0.5 cursor-pointer accent-blue-600"
                  />
                  <label htmlFor="datenschutz-new" className="text-xs text-black/40 leading-relaxed cursor-pointer">
                    {t.form.privacy}{' '}
                    <a href="/datenschutz" className="text-blue-600 hover:underline">
                      {t.form.privacyLink}
                    </a>{' '}
                    {t.form.privacyEnd}
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-blue-600 text-white font-bold rounded-md hover:bg-blue-500 transition-all hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i> {t.form.submitting}
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i> {t.form.submitButton}
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}